import { SESSION_RESULTS_GRAPH_COLORS } from '@/constants/colors';
import { LUDUM_SESSION_RESULTS_LIVE_STATUSES } from '@/constants';
const { DISCARD, IDLE } = LUDUM_SESSION_RESULTS_LIVE_STATUSES;
import { isAthlete, isCoach } from '@/helpers/rolesHelper';
import { isWaterSession } from '@/helpers/sessionHelper';
import { randomHexColorCode } from '@/helpers/util';
import { average, round } from '@/util/numberFormatter';
import { i18n } from '@/lang';
import get from 'get-object-path';

const REFRESH_INTERVAL_IN_SECONDS = 5;

function setColor(index) {
    return SESSION_RESULTS_GRAPH_COLORS[index] || randomHexColorCode();
}

export const sessionResultsLive = {
    namespaced: true,

    state: {
        mainSportId: null,
        sessionParticipants: [],
        sessionCrews: [],
        liveResults: {},
        loaded: false,
        filter: {
            athletes: [],
            crews: [],
            sessions: []
        },
        navigationSport: null,
        sessionDetails: {
            name: null,
            description: null,
            video_stream_url: null,
            venue: {}
        },
        sessionsList: [],
        totalNumberOfRecording: 0,
        club: null
    },

    getters: {
        filter(state) {
            return state.filter;
        },

        filterValues(state, getters) {
            const ids = [
                ...state.filter.crews.map(id => `crew-${id}`),
                ...state.filter.athletes.map(id => `athlete-${id}`),
                ...getters.filterCrewAthletes.map(id => `athlete-${id}`)
            ];
            return [...new Set(ids)];
        },

        loaded(state) {
            return state.loaded;
        },

        liveResults(state) {
            return state.liveResults;
        },

        sportLiveResults(state) {
            if (!state.navigationSport) return state.liveResults;

            return {
                [state.navigationSport]: state.liveResults[state.navigationSport]
            };
        },

        filteredLiveResults(state, getters) {
            const results = _.cloneDeep(getters.sportLiveResults);

            if (!state.filter.athletes.length) return results;

            _.each(results, sportResults => {
                _.each(sportResults, (athleteResult, key) => {
                    if (!state.filter.athletes.includes(Number(key))) {
                        Vue.delete(sportResults, key);
                    }
                });
            });
            return results;
        },

        sessionParticipants(state) {
            return state.sessionParticipants;
        },

        sessionCoaches(state) {
            return state.sessionParticipants.filter(({ user_role_id }) => isCoach(user_role_id));
        },

        sessionAthletes(state) {
            return state.sessionParticipants.filter(({ user_role_id }) => isAthlete(user_role_id));
        },

        isCoach(state, getters, rootState, rootGetters) {
            return getters.sessionCoaches.map(({ user_id }) => user_id).includes(rootGetters['clubHelper/me'].id);
        },

        sessionCrews(state) {
            return state.sessionCrews;
        },

        filteredSessionCrews(state, getters) {
            const crews = !state.filter.crews.length
                ? state.sessionCrews
                : state.sessionCrews.filter(({ id }) => state.filter.crews.includes(id));
            return state.filter.sessions.length
                ? crews.filter(({ id }) => getters.sessionListCrews.includes(id)) || true
                : crews;
        },

        sessionListCrews(state) {
            const sessions = state.sessionsList.filter(({ id }) => state.filter.sessions.includes(id));
            const crews = sessions
                .map(
                    session =>
                        get(session, 'session.data.structure.data[0].pieces.data[0].crews.data') ||
                        get(session, 'crews.data') ||
                        []
                )
                .flat()
                .map(({ id }) => id);
            return [...new Set(crews)];
        },

        crews(state, getters) {
            const crews = _.uniqBy([...getters.crewsWithData, ...getters.crewsWithUserData], 'id');
            return [...crews, ...getters.usersAsCrews];
        },

        crewsWithData(state, getters) {
            return getters.filteredSessionCrews.filter(({ id }) => getters.crewsWithResults.includes(id));
        },

        crewsWithUserData(state, getters) {
            return getters.filteredSessionCrews.filter(crew =>
                (get(crew, 'crew_users.data') || [])
                    .map(({ user_id }) => user_id)
                    .some(id => getters.usersWithResults.includes(id))
            );
        },

        crewsWithResults(state, getters) {
            return Object.values(getters.filteredLiveResults[getters.mainSportId] || {})
                .map(({ crewId }) => crewId)
                .filter(Boolean);
        },

        usersWithResults(state, getters) {
            return Object.values(getters.filteredLiveResults[getters.mainSportId] || {})
                .map(({ userId }) => userId)
                .filter(Boolean);
        },

        usersAsCrews(state, getters) {
            const crewUsers = getters.sessionCrews
                .map(crew => (get(crew, 'crew_users.data') || []).map(({ user_id }) => user_id))
                .flat();
            const uniqueUsers = [...new Set(crewUsers)];
            const usersWithoutCrews = _.filter(
                getters.filteredLiveResults[getters.mainSportId],
                ({ userId }) => userId && !uniqueUsers.includes(userId)
            );
            return usersWithoutCrews.map(({ athlete }) => ({
                id: athlete.id,
                name: athlete.full_name,
                crew_users: { data: [{ user_id: athlete.id }] },
                boat_type: { data: { name: '1x' } }
            }));
        },

        mainSportId(state) {
            return state.mainSportId;
        },

        navigationSport(state) {
            return state.navigationSport;
        },

        singleSport(state) {
            const sports = Object.keys(state.liveResults) || [];
            return !sports.length || (sports.length === 1 && Number(sports[0]) === state.mainSportId);
        },

        sessionDetails(state) {
            return state.sessionDetails;
        },

        sessionsList(state) {
            return state.sessionsList;
        },

        filterCrewAthletes(state) {
            if (!state.filter.crews.length) return [];
            const crewUsers = state.sessionCrews
                .filter(({ id }) => state.filter.crews.includes(id))
                .map(crew => get(crew, 'crew_users.data'))
                .flat();
            return [...new Set(crewUsers.map(({ user_id }) => user_id))];
        },

        clubGmtPaddling(state) {
            return get(state, 'club.paddle_gmt_distribution.data') || [];
        },

        clubGmtRowing(state) {
            return get(state, 'club.gmt_distribution.data') || [];
        }
    },

    mutations: {
        reset(state) {
            state = {
                mainSportId: null,
                sessionParticipants: [],
                sessionCrews: [],
                liveResults: {},
                loaded: false,
                filter: {
                    athletes: [],
                    crews: [],
                    sessions: []
                },
                navigationSport: null,
                sessionDetails: {
                    name: null,
                    description: null,
                    video_stream_url: null,
                    venue: {}
                },
                sessionsList: [],
                totalNumberOfRecording: 0,
                club: null
            };
        },

        resetLiveResults(state) {
            state.liveResults = {};
        },

        setLoaded(state, payload) {
            state.loaded = payload;
        },

        setSessionParticipants(state, payload) {
            const sessionParticipants = _.uniqBy([...state.sessionParticipants, ...payload], 'user_id');
            state.sessionParticipants = Object.freeze(sessionParticipants);
        },

        setSessionCrews(state, payload) {
            const crews = [...state.sessionCrews, ...payload];
            state.sessionCrews = Object.freeze(_.uniqBy(crews, 'id'));
        },

        setMainSportId(state, payload) {
            state.mainSportId = payload;
        },

        setNavigationSport(state, payload) {
            state.navigationSport = payload;
        },

        removeLiveResults(state, { user_id, crew_id }) {
            if (user_id && user_id != 0) {
                _.each(state.liveResults, (result, sport) => {
                    Vue.delete(result, user_id);
                    !Object.values(result).length && Vue.delete(state.liveResults, sport);
                });
            }

            if (crew_id && crew_id != 0) {
                _.each(state.liveResults, (result, sport) => {
                    Vue.delete(result, 'crew-' + crew_id);
                    !Object.values(result).length && Vue.delete(state.liveResults, sport);
                });
            }
        },

        addLiveResults(state, payload) {
            (payload.data || []).forEach(data => {
                const formattedEvent = Object.freeze(JSON.parse(data));
                const sportId = formattedEvent.sport || state.mainSportId;

                !state.liveResults[sportId] && Vue.set(state.liveResults, sportId, {});

                const uniqueId = payload.user_id ? `athlete-${payload.user_id}` : `crew-${payload.crew_id}`;
                const userResults = state.liveResults[sportId][uniqueId];

                if (!userResults) {
                    _.each(state.liveResults, (result, resultSport) => {
                        if (sportId !== Number(resultSport)) {
                            const lastEventStatus = get(result[uniqueId] || {}, 'lastEvent.status');
                            if (lastEventStatus === IDLE) {
                                Vue.delete(result, uniqueId);
                                !Object.values(result).length && Vue.delete(state.liveResults, resultSport);
                            }
                        }
                    });

                    const athlete = state.sessionParticipants.find(({ user_id }) => user_id === payload.user_id);
                    const formattedAthlete = athlete ? get(athlete, 'user.data') : { id: payload.user_id };

                    Vue.set(state.liveResults[sportId], uniqueId, {
                        uniqueId,
                        id: payload.id,
                        userId: payload.user_id,
                        crewId: payload.crew_id,
                        lastEvent: formattedEvent,
                        athlete: Object.freeze(formattedAthlete),
                        sportId,
                        color: setColor(state.totalNumberOfRecording),
                        name: payload.user_id
                            ? formattedAthlete.full_name
                            : (state.sessionCrews.find(({ id }) => id === payload.crew_id) || {}).name,
                        delayedData: {
                            events: [formattedEvent],
                            eventCounter: 1,
                            speed: null,
                            stroke_rate: null
                        }
                    });
                    state.totalNumberOfRecording += 1;
                } else {
                    state.liveResults[sportId][uniqueId].lastEvent = formattedEvent;
                    if (isWaterSession(sportId)) {
                        const { delayedData } = state.liveResults[sportId][uniqueId];
                        const { events } = delayedData;
                        events.length >= REFRESH_INTERVAL_IN_SECONDS && events.shift();
                        events.push(formattedEvent);
                        delayedData.eventCounter++;
                        if (delayedData.eventCounter % REFRESH_INTERVAL_IN_SECONDS === 0) {
                            delayedData.speed = round(average(events.map(({ speed }) => Number(speed))));
                            delayedData.stroke_rate = round(average(events.map(({ stroke_rate }) => stroke_rate)), 0);
                        }
                    }
                }
            });
        },

        setAthletes(state, payload = []) {
            state.filter.athletes = payload;
        },

        setCrews(state, payload = []) {
            state.filter.crews = payload;
        },

        selectCrew(state, crewId) {
            const index = state.filter.crews.indexOf(crewId);
            index === -1 && state.filter.crews.push(crewId);
        },

        deselectAllCrews(state) {
            state.filter.crews = [];
        },

        deselectCrew(state, crewId) {
            const index = state.filter.crews.indexOf(crewId);
            index !== -1 && state.filter.crews.splice(index, 1);
        },

        setSessionDetails(state, session) {
            const { name, description, video_stream_url, venue, public_page_intro } = session;
            state.sessionDetails = {
                name,
                description,
                video_stream_url,
                public_page_intro,
                venue: (venue && venue.data) || {}
            };
        },

        setSessionsList(state, argSession) {
            const session = !!state.sessionsList.find(({ id }) => id === argSession.id);
            !session && state.sessionsList.push(argSession);
        },

        selectSession(state, sessionId) {
            const index = state.filter.sessions.indexOf(sessionId);
            index === -1 && state.filter.sessions.push(sessionId);
        },

        deselectAllSessions(state) {
            state.filter.sessions = [];
        },

        deselectSession(state, sessionId) {
            const index = state.filter.sessions.indexOf(sessionId);
            index !== -1 && state.filter.sessions.splice(index, 1);
        },

        setClub(state, club) {
            state.club = club;
        }
    },

    actions: {
        addInitialResults({ dispatch }, payload) {
            payload.forEach(event => dispatch('addLiveResults', event));
        },

        removeLiveResults({ commit, dispatch, state }, payload) {
            commit('removeLiveResults', payload);
            if (payload.user_id) {
                const user = state.sessionParticipants.find(({ user_id }) => user_id === payload.user_id);
                if (!user) return;

                const message = i18n.t('athleteDiscardRecording', { athlete: get(user, 'user.data.full_name') });
                dispatch('notifyError', { message }, { root: true });
            }

            if (payload.crew_id) {
                const crew = state.sessionCrews.find(({ id }) => id === payload.crew_id);
                if (!crew) return;

                const message = i18n.t('athleteDiscardRecording', { athlete: crew.name });
                dispatch('notifyError', { message }, { root: true });
            }
        },

        addLiveResults({ commit, dispatch, state }, payload) {
            (payload.data || []).forEach(item => {
                const { status } = JSON.parse(item);
                const shouldDelete = status === DISCARD;
                shouldDelete ? dispatch('removeLiveResults', payload) : commit('addLiveResults', payload);
            });
        },

        getSession({ dispatch, commit }, uuid) {
            return dispatch(
                'get',
                {
                    url: `${api}public/event/${uuid}?fields=id|name|sport|uuid|video_stream_url|description|public_page_intro`,
                    params: {
                        include: [
                            'club',
                            'crews.gmt_score',
                            'crews.crew_users:fields(id|user_id|position)',
                            'crews.crew_users.user:fields(id|first_name|last_name|full_name|picture)',
                            'session:fields(id)',
                            'session.structure:fields(pieces)',
                            'session.structure.pieces:fields(id)',
                            'session.structure.pieces.crews:fields(id|name|boat_type|description|style)',
                            'session.structure.pieces.crews.gmt_score',
                            'session.structure.pieces.crews.crew_users:fields(id|user_id|position)',
                            'session.structure.pieces.crews.crew_users.user:fields(id|first_name|last_name|full_name|picture)',
                            'participants:fields(id|user_id|user_role_id)',
                            'participants.user:fields(id|first_name|last_name|full_name|picture|hr_zones|max_hr)',
                            'venue'
                        ].join(',')
                    }
                },
                { root: true }
            )
                .then(response => {
                    const participants = get(response.data, 'participants.data') || [];
                    commit('setSessionParticipants', participants);

                    const crews =
                        get(response.data, 'session.data.structure.data[0].pieces.data[0].crews.data') ||
                        get(response.data, 'crews.data') ||
                        [];
                    commit('setSessionCrews', crews);
                    commit('setMainSportId', get(response.data, 'sport.id'));
                    commit('setSessionDetails', response.data);
                    commit('setClub', get(response.data, 'club.data'));
                    commit('resetLiveResults');
                    return response;
                })
                .catch(error => error);
        },

        fetchLastLiveResults({ dispatch, commit }, uuid) {
            return dispatch(
                'get',
                {
                    url: `${api}public/live-results/show-last/${uuid}`
                },
                { root: true }
            )
                .then(response => {
                    dispatch('addInitialResults', response.data.data);
                    return response;
                })
                .catch(error => error);
        },

        sendMessage(
            { dispatch, commit, rootGetters },
            { sessionId, userIds = [], message = '', messageThreadId, status, crewIds = [] }
        ) {
            return dispatch(
                'post',
                {
                    url: api + 'message/create-message',
                    data: {
                        event_id: sessionId,
                        user_ids: userIds.filter(Boolean),
                        message: message,
                        message_thread_id: messageThreadId,
                        live_results_status: status,
                        crew_ids: crewIds.filter(Boolean)
                    }
                },
                {
                    root: true
                }
            );
        }
    }
};

export default sessionResultsLive;
